<template>
  <div class="cart-page">
    <h1>Shopping Cart</h1>
    
    <div v-if="!cartItems.length" class="empty-cart">
      <i class="fas fa-shopping-cart"></i>
      <p>Your cart is empty</p>
      <router-link to="/" class="continue-shopping">Continue Shopping</router-link>
    </div>

    <div v-else class="cart-items">
      <div v-for="item in cartItems" :key="item.id" class="cart-item" :class="{ 'is-sold-out': isItemSoldOut(item) }">
        <div class="item-image">
          <img :src="item.image" :alt="item.title" @error="handleImageError">
          <div v-if="isItemSoldOut(item)" class="sold-out-badge">Sold Out</div>
        </div>
        <div class="item-details">
          <h3 class="truncate-title">{{ item.title }}</h3>
          <p class="seller">Seller: {{ item.sellerName }}</p>
          <p class="price">{{ item.price }}</p>
        </div>
        
        <div class="item-total">
          {{ calculateItemTotal(item) }}
        </div>
        <button class="remove-item" @click="removeFromCart(item)">
          <i class="fas fa-trash"></i>
        </button>
        <div :data-product-id="item.productId" :data-category-id="item.categoryId" style="display: none;"></div>
      </div>

      <div class="checkout-section">
        <div v-if="isAuthenticated" class="address-section">
          <h2>Shipping Address</h2>
          <div v-if="addresses.length > 0" class="address-list">
            <div v-for="address in addresses" 
                 :key="address.id" 
                 class="address-card"
                 :class="{ 'selected': selectedAddressId === address.id }"
                 @click="selectAddress(address.id)">
              <div class="address-details">
                <h3>{{ address.name }}</h3>
                <p>{{ address.street_address }}</p>
                <p>{{ address.city }}, {{ address.state }} {{ address.postal_code }}</p>
                <p>{{ address.country }}</p>
              </div>
              <div class="address-select">
                <div class="radio-button" :class="{ 'selected': selectedAddressId === address.id }"></div>
              </div>
            </div>
          </div>
          <div v-else class="no-address">
            <p>No shipping address found</p>
            <router-link to="/addresses" class="add-address-btn">
              <i class="fas fa-plus"></i> Add New Address
            </router-link>
          </div>
        </div>

        <div class="cart-summary">
          <div class="subtotal">
            <span>SUBTOTAL</span>
            <span class="subtotal-amount">€{{ cartSubtotal.toFixed(2) }}</span>
          </div>
          <div class="shipping">
            <div class="shipping-label">
              <span>SHIPPING</span>
              <!-- <span class="shipping-type">FLAT RATE:</span> -->
            </div>
            <span class="shipping-amount">€{{ shippingCost.toFixed(2) }}</span>
          </div>
          <div class="total">
            <span>TOTAL</span>
            <span class="total-amount">€{{ finalTotal.toFixed(2) }}</span>
          </div>
          <button class="checkout-button" 
                  @click="proceedToCheckout" 
                  :disabled="isLoading"
                  :class="{ 'loading': isLoading }">
            <!-- TODO: restore :disabled="isLoading || hasSoldOutItems" and :class="{ 'loading': isLoading, 'is-blocked': hasSoldOutItems && !isLoading }" -->
            <!-- TODO: restore button text: hasSoldOutItems ? 'Remove Sold Out Items' : ... -->
            <span v-if="!isLoading">{{ isAuthenticated ? 'Proceed to Checkout' : 'Login to Checkout' }}</span>
            <span v-else class="spinner"></span>
          </button>
          <p v-if="hasSoldOutItems" class="sold-out-warning">
            Remove sold out items before checkout.
          </p>
          <p v-if="isAuthenticated && !selectedAddressId" class="address-warning">
            Please select a shipping address
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';
import itemStatusUtils from '../utils/itemStatus';
import internalDummyData from '../utils/internalDummyData';

export default {
  name: 'Cart',
  data() {
    return {
      addresses: [],
      selectedAddressId: null,
      isLoading: false,
      cart: [],
      itemStatusByKey: {},
      internalDummyMode: false,
      dismissedDummyItemIds: {}
    }
  },
  computed: {
    isAuthenticated() {
      return this.$store.state.auth.isAuthenticated;
    },
    user() {
      return this.$store.state.auth.user;
    },
    cartItems() {
      const realItems = this.$store.state.cart.map(item => ({
        ...item,
        productId: item.productId || this.extractProductId(item.url || ''),
        categoryId: item.categoryId || this.extractCategoryId(item.url || '')
      }));

      if (!this.internalDummyMode) return realItems;

      const dismissed = this.dismissedDummyItemIds || {};
      const dummyItems = internalDummyData
        .getDummyCartItems()
        .filter((item) => !dismissed[item.id]);

      return [...dummyItems, ...realItems];
    },
    cartSubtotal() {
      if (!this.internalDummyMode) return this.$store.getters.cartTotal;

      return this.cartItems.reduce((total, item) => {
        const numericPrice = parseFloat(String(item.price || '').replace(/[^0-9.-]+/g, ''));
        const safePrice = Number.isFinite(numericPrice) ? numericPrice : 0;
        const quantity = Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : 1;
        return total + (safePrice * quantity);
      }, 0);
    },
    shippingCost() {
      if (this.cartItems.length === 0) return 0;
      // Base shipping cost is 25€ for first item
      // Additional 10€ for each extra item
      return 25 + (Math.max(0, this.cartItems.length - 1) * 20);
    },
    finalTotal() {
      return this.cartSubtotal + this.shippingCost;
    },
    hasSoldOutItems() {
      return this.cartItems.some(item => this.isItemSoldOut(item));
    }
  },
  methods: {
    handleImageError(e) {
      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
    },
    calculateItemTotal(item) {
      const price = parseFloat(item.price.replace(/[^0-9.-]+/g, ''));
      return '€' + (price * item.quantity).toFixed(2);
    },
    normalizeItemStatus(value) {
      return itemStatusUtils.normalizeItemStatus(value);
    },
    getItemStatusKey(item) {
      return itemStatusUtils.buildItemStatusKey(
        item,
        (url) => this.extractProductId(url),
        (url) => this.extractCategoryId(url)
      );
    },
    isItemSoldOut(item) {
      return itemStatusUtils.isCartItemSoldOut(
        item,
        this.itemStatusByKey,
        (url) => this.extractProductId(url),
        (url) => this.extractCategoryId(url)
      );
    },
    async fetchItemStatus(item) {
      const productId = item?.productId || this.extractProductId(item?.url || '');
      const categoryId = item?.categoryId || this.extractCategoryId(item?.url || '') || 0;
      if (!productId) return null;

      const response = await axios.post(
        `${config.apiBaseUrl}/api/item?id=${encodeURIComponent(productId)}&categoryId=${encodeURIComponent(categoryId || 0)}`,
        {},
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const responseData = response?.data || {};
      return itemStatusUtils.extractItemStatusFromItemApiResponse(responseData);
    },
    async refreshCartItemStatuses() {
      const items = Array.isArray(this.cartItems) ? this.cartItems : [];
      if (!items.length) {
        this.itemStatusByKey = {};
        return;
      }

      const nextStatusByKey = { ...this.itemStatusByKey };

      await Promise.all(items.map(async (item) => {
        const key = this.getItemStatusKey(item);
        if (!key) return;

        const localStatus = this.normalizeItemStatus(item?.itemStatus);
        if (localStatus !== null) {
          nextStatusByKey[key] = localStatus;
          return;
        }

        try {
          const fetchedStatus = await this.fetchItemStatus(item);
          if (fetchedStatus !== null) {
            nextStatusByKey[key] = fetchedStatus;
          }
        } catch (error) {
          console.warn('Failed to refresh item status for cart item:', key, error);
        }
      }));

      const validKeys = new Set(items.map((item) => this.getItemStatusKey(item)).filter(Boolean));
      Object.keys(nextStatusByKey).forEach((key) => {
        if (!validKeys.has(key)) delete nextStatusByKey[key];
      });

      this.itemStatusByKey = nextStatusByKey;
    },
    increaseQuantity(item) {
      this.$store.commit('UPDATE_QUANTITY', {
        productId: item.id,
        quantity: item.quantity + 1
      });
    },
    decreaseQuantity(item) {
      if (item.quantity > 1) {
        this.$store.commit('UPDATE_QUANTITY', {
          productId: item.id,
          quantity: item.quantity - 1
        });
      }
    },
    removeFromCart(productId) {
      if (productId && typeof productId === 'object') {
        if (productId.__internalDummy) {
          this.$set(this.dismissedDummyItemIds, productId.id, true);
          return;
        }
        this.$store.commit('REMOVE_FROM_CART', productId.id);
        return;
      }

      this.$store.commit('REMOVE_FROM_CART', productId);
    },
    async checkSession() {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, {
          withCredentials: true // Important for sending cookies
        });
        
        if (response.data.isAuthenticated && response.data.user) {
          this.$store.commit('SET_AUTH', {
            isAuthenticated: true,
            user: response.data.user,
            token: null // Session-based auth doesn't use tokens
          });
          return true;
        } else {
          this.$store.commit('CLEAR_AUTH');
          return false;
        }
      } catch (error) {
        console.error('Session check error:', error);
        this.$store.commit('CLEAR_AUTH');
        return false;
      }
    },
    async fetchAddresses() {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/addresses?_t=${Date.now()}`, {
          withCredentials: true,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        // Handle the response data
        if (response.data) {
          // Convert object to array if needed
          if (typeof response.data === 'object' && !Array.isArray(response.data)) {
            // Remove _cachedAt property and convert to array
            const { _cachedAt, ...addressesObj } = response.data;
            this.addresses = Object.values(addressesObj);
          } else {
            this.addresses = response.data;
          }
          
          // First check for default address
          const defaultAddress = this.addresses.find(addr => addr.is_default === 1);
          if (defaultAddress) {
            this.selectedAddressId = defaultAddress.id;
          }
          // If no default address but only one address exists, select it
          else if (this.addresses.length === 1) {
            this.selectedAddressId = this.addresses[0].id;
          }
        } else {
          this.addresses = [];
          this.selectedAddressId = null;
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
        this.addresses = [];
        this.selectedAddressId = null;
        this.showToast('Failed to load addresses');
      }
    },
    selectAddress(addressId) {
      this.selectedAddressId = addressId;
    },
    showToast(message, type = 'error') {
      // Emit the toast event to the root component
      this.$root.$emit('show-toast', { message, type });
    },
    extractProductId(url) {
      const params = new URLSearchParams(url.split('?')[1]);
      return params.get('id') || '';
    },
    extractCategoryId(url) {
      const params = new URLSearchParams(url.split('?')[1]);
      return params.get('categoryId') || '';
    },
    async proceedToCheckout() {
      try {
        await this.refreshCartItemStatuses();
        // TODO: restore sold-out guard below
        // if (this.hasSoldOutItems) {
        //   this.showToast('Please remove sold out items before checkout', 'error');
        //   return;
        // }

        this.isLoading = true;

        // Check session with credentials
        const isAuthenticated = await this.checkSession();
        
        if (!isAuthenticated) {
          this.showToast('Please login to continue with checkout', 'error');
          this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
          return;
        }

        // Verify we have a valid user
        if (!this.user || !this.user.id) {
          this.showToast('User session is invalid. Please login again.', 'error');
          this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
          return;
        }

        if (!this.selectedAddressId) {
          this.showToast('Please select a shipping address', 'error');
          return;
        }

        // Simulate cart validation
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Show processing message
        this.showToast('Validating cart items...', 'info');

        // Store the cart with URL parameters in localStorage
        const cartWithParams = this.cartItems.map(item => ({
          ...item,
          productId: item.productId || this.extractProductId(item.url || ''),
          categoryId: item.categoryId || this.extractCategoryId(item.url || '')
        }));
        
        localStorage.setItem('cartWithParams', JSON.stringify(cartWithParams));
        localStorage.setItem('selectedAddressId', this.selectedAddressId);

        // Simulate final processing
        await new Promise(resolve => setTimeout(resolve, 700));

        // Show success message before redirecting
        this.showToast('Proceeding to checkout...', 'success');
        
        // Add small delay before redirect for better UX
        await new Promise(resolve => setTimeout(resolve, 500));
        
        this.$router.push('/checkout');
      } catch (error) {
        console.error('Checkout error:', error);
        this.showToast('Failed to proceed to checkout. Please try again.', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    addToCart(item) {
      const cartItem = {
        id: item.id,
        productId: item.id, // Store productId
        categoryId: item.category_id, // Store categoryId
        title: item.title,
        price: item.price,
        image: item.image,
        quantity: 1,
        sellerName: item.seller_name
      };
      this.$store.commit('ADD_TO_CART', cartItem);
      // Save to localStorage with all necessary fields
      const cartWithParams = JSON.parse(localStorage.getItem('cartWithParams') || '[]');
      cartWithParams.push(cartItem);
      localStorage.setItem('cartWithParams', JSON.stringify(cartWithParams));
    }
  },
  async created() {
    this.internalDummyMode = internalDummyData.hasInternalDummyDataTrigger();
    // Check session with credentials
    await this.checkSession();
    if (this.isAuthenticated) {
      await this.fetchAddresses();
    }
    await this.refreshCartItemStatuses();
  },
  watch: {
    cartItems: {
      immediate: true,
      async handler() {
        await this.refreshCartItemStatuses();
      }
    },
    isAuthenticated: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.selectedAddressId = null;
          this.addresses = [];
        }
      }
    }
  }
}
</script>

<style scoped>
.cart-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  margin-bottom: 30px;
  text-align: center;
}

.empty-cart {
  text-align: center;
  padding: 50px 0;
}

.empty-cart i {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 20px;
}

.continue-shopping {
  display: inline-block;
  padding: 10px 20px;
  background: #000;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  margin-top: 20px;
}

.cart-items {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.cart-item {
  display: grid;
  grid-template-columns: 120px 1fr 120px 120px 50px;
  gap: 20px;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  position: relative;
}

.item-image {
  width: 100px;
  height: 100px;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.item-details h3.truncate-title {
  margin: 0 0 10px;
  font-size: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
  line-height: 1.4;
}

.seller {
  color: #666;
  font-size: 0.9rem;
}

.price {
  font-weight: bold;
  color: #e44d26;
}

.item-quantity {
  display: flex;
  align-items: center;
  gap: 10px;
}

.item-quantity button {
  width: 30px;
  height: 30px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
}

.item-quantity button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.item-total {
  font-weight: bold;
}

.cart-item.is-sold-out {
  background: #f9fafc;
}

.cart-item.is-sold-out .item-image img {
  filter: grayscale(100%) saturate(0.15) contrast(0.92);
  transform: scale(1.01);
}

.cart-item.is-sold-out .item-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(71, 78, 90, 0.42), rgba(60, 67, 80, 0.5)),
    repeating-linear-gradient(
      -45deg,
      rgba(238, 241, 247, 0.08) 0,
      rgba(238, 241, 247, 0.08) 4px,
      rgba(86, 94, 110, 0.08) 4px,
      rgba(86, 94, 110, 0.08) 8px
    );
  pointer-events: none;
  z-index: 1;
}

.sold-out-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2f3748;
  background: rgba(230, 233, 239, 0.9);
  border: 1px solid rgba(126, 136, 153, 0.78);
  box-shadow: 0 6px 12px rgba(41, 48, 63, 0.22);
  backdrop-filter: blur(1px);
  pointer-events: none;
  white-space: nowrap;
}

.remove-item {
  border: none;
  background: none;
  color: #999;
  cursor: pointer;
  padding: 5px;
}

.remove-item:hover {
  color: #e44d26;
}

.checkout-section {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 30px;
  margin-top: 30px;
  border-top: 1px solid #eee;
  padding-top: 30px;
}

.address-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 20px;
}

.address-section h2 {
  font-size: 1.1rem;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.address-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.address-card:hover {
  border-color: #000;
}

.address-card.selected {
  border-color: #000;
  background-color: #f8f9fa;
}

.address-details h3 {
  font-size: 1rem;
  margin-bottom: 8px;
}

.address-details p {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 4px;
  line-height: 1.4;
}

.radio-button {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 50%;
  position: relative;
  transition: all 0.3s ease;
}

.radio-button.selected {
  border-color: #000;
}

.radio-button.selected:after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  background: #000;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.address-warning {
  text-align: center;
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 10px;
}

.checkout-button {
  margin-top: 15px;
  width: 100%;
  padding: 15px 24px;
  background: #000;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  min-height: 52px;
  min-width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.checkout-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.checkout-button.is-blocked {
  background: #9ca3af;
}

.checkout-button.loading {
  background: #333;
  cursor: wait;
}

.checkout-button .spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: button-loading-spinner 0.8s linear infinite;
  display: inline-block;
}

@keyframes button-loading-spinner {
  from {
    transform: rotate(0turn);
  }
  to {
    transform: rotate(1turn);
  }
}

.checkout-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.checkout-button.loading:after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  top: 50%;
  left: 50%;
  margin: -10px 0 0 -10px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: button-loading-spinner 1s linear infinite;
}

@keyframes button-loading-spinner {
  from {
    transform: rotate(0turn);
  }
  to {
    transform: rotate(1turn);
  }
}

.cart-summary {
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 400px;
  margin-left: auto;
}

.cart-summary > div {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: #333;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.subtotal {
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.shipping {
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.shipping-label {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.shipping-type {
  font-size: 0.85rem;
  color: #666;
  margin-top: 2px;
}

.sold-out-warning {
  margin: 8px 0 0;
  color: #b91c1c;
  text-align: center;
  font-size: 0.9rem;
}

.total {
  font-size: 0.9rem !important;
  padding-top: 10px;
}

.subtotal-amount,
.shipping-amount,
.total-amount {
  color: #333 !important;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .checkout-section {
    grid-template-columns: 1fr;
  }

  .cart-summary {
    max-width: none;
  }

  .cart-page {
    padding: 15px;
  }

  h1 {
    font-size: 1.5rem;
    margin-bottom: 20px;
  }

  .cart-item {
    grid-template-columns: 100px 1fr 50px;
    gap: 15px;
    padding: 15px;
  }

  .item-image {
    width: 80px;
    height: 80px;
  }

  .sold-out-badge {
    font-size: 10px;
    padding: 5px 11px;
  }

  .item-details h3.truncate-title {
    font-size: 0.9rem;
    -webkit-line-clamp: 2;
    max-width: 100%;
  }

  .item-total {
    display: none;
  }

  .cart-summary {
    padding: 20px 15px;
    max-width: 100%;
  }

  .checkout-button {
    min-width: 100%;
    padding: 12px 20px;
  }
}

@media (max-width: 480px) {
  .address-card {
    padding: 12px;
  }

  .address-details h3 {
    font-size: 0.9rem;
  }

  .address-details p {
    font-size: 0.8rem;
  }

  .cart-summary {
    padding: 15px 10px;
  }

  .cart-summary > div {
    font-size: 0.85rem;
  }

  .shipping-type {
    font-size: 0.75rem;
  }

  .checkout-button {
    padding: 12px 20px;
    font-size: 0.9rem;
  }
}

@media (max-width: 360px) {
  .cart-item {
    grid-template-columns: 70px 1fr 35px;
    gap: 8px;
  }

  .item-image {
    width: 60px;
    height: 60px;
  }

  .sold-out-badge {
    font-size: 9px;
    padding: 4px 9px;
  }

  .item-details h3.truncate-title {
    font-size: 0.85rem;
    -webkit-line-clamp: 2;
  }

  .seller {
    font-size: 0.75rem;
  }

  .price {
    font-size: 0.85rem;
  }

  .cart-summary > div {
    font-size: 0.8rem;
  }

  .shipping-type {
    font-size: 0.7rem;
  }

  .empty-cart {
    padding: 30px 0;
  }

  .empty-cart i {
    font-size: 36px;
  }
}
</style> 